import React from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ConsentStep = ({ formData, errors, onChange }) => {
  const handleCheckboxChange = (name, checked) => {
    onChange({ target: { name, value: checked } });
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
          Terms & Consent
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">
          Please review and accept our terms to complete your registration.
        </p>
      </div>
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 max-h-[300px] md:max-h-[400px] overflow-y-auto">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
          Terms of Service
        </h3>
        <div className="space-y-4 text-xs md:text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">1. Acceptance of Terms</strong><br />
            By using LoveNudge, you agree to these Terms of Service and our Privacy Policy. These terms govern your use of our intimate relationship enhancement platform.
          </p>
          <p>
            <strong className="text-foreground">2. User Eligibility</strong><br />
            You must be at least 18 years old and in a committed relationship to use this service. Both partners must consent to participate.
          </p>
          <p>
            <strong className="text-foreground">3. Privacy & Data Protection</strong><br />
            We take your privacy seriously. All intimate communications are encrypted end-to-end. Your personal desires and preferences are never shared without your explicit consent. We comply with HIPAA standards for data protection.
          </p>
          <p>
            <strong className="text-foreground">4. AI Communication</strong><br />
            Our AI system mediates communications between partners to facilitate better understanding. The AI does not store or share your raw inputs with third parties. All AI processing happens within our secure infrastructure.
          </p>
          <p>
            <strong className="text-foreground">5. User Responsibilities</strong><br />
            You agree to use LoveNudge respectfully and honestly. You will not use the platform to harass, manipulate, or harm your partner. You are responsible for maintaining the confidentiality of your account credentials.
          </p>
          <p>
            <strong className="text-foreground">6. Content Guidelines</strong><br />
            While we support open communication about intimate desires, you agree not to share illegal content or content that violates consent principles.
          </p>
          <p>
            <strong className="text-foreground">7. Termination</strong><br />
            Either partner can terminate their account at any time. Upon termination, your data will be deleted according to our data retention policy.
          </p>
          <p>
            <strong className="text-foreground">8. Limitation of Liability</strong><br />
            LoveNudge is a communication tool and not a substitute for professional relationship counseling or therapy. We are not liable for relationship outcomes.
          </p>
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 max-h-[300px] md:max-h-[400px] overflow-y-auto">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
          Privacy Policy
        </h3>
        <div className="space-y-4 text-xs md:text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Data Collection</strong><br />
            We collect personal information (name, email, age), relationship data, mood tracking information, scheduling preferences, and intimate communication content. All data is encrypted and stored securely.
          </p>
          <p>
            <strong className="text-foreground">Data Usage</strong><br />
            Your data is used solely to provide and improve our service. We use AI to analyze compatibility and suggest optimal encounter times. We never sell your data to third parties.
          </p>
          <p>
            <strong className="text-foreground">Data Sharing</strong><br />
            Data is shared between partners only according to your privacy preferences. We may share anonymized, aggregated data for research purposes with your consent.
          </p>
          <p>
            <strong className="text-foreground">Data Security</strong><br />
            We employ industry-standard encryption, regular security audits, and HIPAA-compliant data handling practices. Your intimate communications are end-to-end encrypted.
          </p>
          <p>
            <strong className="text-foreground">Your Rights</strong><br />
            You have the right to access, modify, or delete your data at any time. You can export your data or request complete account deletion.
          </p>
        </div>
      </div>
      <div className="space-y-4">
        <Checkbox
          label="I have read and agree to the Terms of Service"
          checked={formData?.acceptTerms}
          onChange={(e) => handleCheckboxChange('acceptTerms', e?.target?.checked)}
          error={errors?.acceptTerms}
          required
        />

        <Checkbox
          label="I have read and agree to the Privacy Policy"
          checked={formData?.acceptPrivacy}
          onChange={(e) => handleCheckboxChange('acceptPrivacy', e?.target?.checked)}
          error={errors?.acceptPrivacy}
          required
        />

        <Checkbox
          label="I consent to AI-mediated communication with my partner"
          description="I understand that AI will process and translate my intimate communications"
          checked={formData?.consentAICommunication}
          onChange={(e) => handleCheckboxChange('consentAICommunication', e?.target?.checked)}
          error={errors?.consentAICommunication}
          required
        />

        <Checkbox
          label="I am at least 18 years old and in a committed relationship"
          checked={formData?.confirmAge}
          onChange={(e) => handleCheckboxChange('confirmAge', e?.target?.checked)}
          error={errors?.confirmAge}
          required
        />

        <Checkbox
          label="I consent to receive email communications from LoveNudge"
          description="You can unsubscribe at any time"
          checked={formData?.emailConsent}
          onChange={(e) => handleCheckboxChange('emailConsent', e?.target?.checked)}
        />
      </div>
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-5">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs md:text-sm text-foreground">
              <strong>Your Privacy is Protected:</strong> All consents can be modified in your account settings. You maintain full control over your data and how it's used.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentStep;